import { Component, computed, effect, inject, input, signal, viewChildren } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { form, FormField, required, validate } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { CenterObject } from '@libs/types/src/organization/object-type/center.object';
import { OrganizationObject } from '@libs/types/src/organization/object-type/organization.object';
import { PlatformBillingBasisStateEnum } from '@libs/types/src/services/enum/platform-billing/platform-billing-basis-state.enum';
import { PlatformBillingContractStateEnum } from '@libs/types/src/services/enum/platform-billing/platform-billing-contract-state.enum';
import { PlatformBillingInvoiceStateEnum } from '@libs/types/src/services/enum/platform-billing/platform-billing-invoice-state.enum';
import { PlatformBillingDeliveryStateEnum } from '@libs/types/src/services/enum/platform-billing/platform-billing-delivery-state.enum';
import { PlatformBillingPeriodEnum } from '@libs/types/src/services/enum/platform-billing/platform-billing-period.enum';
import { PlatformBillingBasisObject } from '@libs/types/src/services/object-type/platform-billing/platform-billing-basis.object';
import { PlatformBillingInvoiceObject } from '@libs/types/src/services/object-type/platform-billing/platform-billing-invoice.object';
import { isAllowed, translate } from '../../../../../shared/shared.global-service';
import { downloadFile } from '../../../../../shared/helpers/file-download.helper';
import { FormatNumberPipe } from '../../../../../shared/pipes/format-number.pipe';
import { successDeleted, successUpdated } from '../../../../content.error';
import { ConfirmBuilder } from '../../../../../shared/service/confirm.builder';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { dateField, numberField, textColumn, userChipField, validFromColumn, validToColumn } from '../../../../../shared/components/kit/table/table.fields';
import { TableColumn, TableColumnType, TableDeleteEvent } from '../../../../../shared/components/kit/table/table.types';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { SaveButtonComponent } from '../../../../../../components/kit/button/save-button.component';
import { TagsInputComponent } from '../../../../../../components/kit/input/tags-input-v2/tags-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input-v2/select-input.component';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input-v2/check-input.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input-v2/date-input.component';
import { WarnFloatMessageComponent } from '../../../../../../components/kit/float-message/warn-float-message.component';
import { LoadingComponent } from '../../../../../../components/kit/loading/loading.component';
import { UserChipRenderTypeEnum } from '../../../../../../components/kit/user-chip/user-chip.component.types';
import { BaseDialogService } from '../../../../../../components/kit/base-dialog/base-dialog-service';
import { OrganizationPlatformBillingStore } from './organization-platform-billing.store';
import { OrganizationPlatformBillingBasisCreateDialogComponent } from './organization-platform-billing-basis-create-dialog/organization-platform-billing-basis-create-dialog.component';
import { OrganizationPlatformBillingContractUpsertDialogComponent } from './organization-platform-billing-contract-upsert-dialog/organization-platform-billing-contract-upsert-dialog.component';
import { OrganizationPlatformBillingInvoiceCreateDialogComponent } from './organization-platform-billing-invoice-create-dialog/organization-platform-billing-invoice-create-dialog.component';
import {
    PlatformBillingBasisTableItem,
    PlatformBillingContractTableItem,
    PlatformBillingInvoiceItemTableItem,
    PlatformBillingInvoiceTableItem,
    PlatformBillingProfileFormModel,
    PlatformBillingTariffSelectOption,
} from './organization-platform-billing.types';

@Component({
    selector: 'app-organization-platform-billing',
    templateUrl: './organization-platform-billing.component.html',
    styleUrl: './organization-platform-billing.component.scss',
    providers: [OrganizationPlatformBillingStore],
    imports: [
        FormField,
        TranslatePipe,
        ButtonComponent,
        SaveButtonComponent,
        TagsInputComponent,
        SelectInputComponent,
        CheckInputComponent,
        DateInputComponent,
        WarnFloatMessageComponent,
        TableComponent,
        PrimeTemplate,
        FormatNumberPipe,
        LoadingComponent,
    ],
})
export class OrganizationPlatformBillingComponent {
    public readonly organizationId = input.required<number>();
    public readonly organization = input<OrganizationObject | null>(null);

    protected readonly store = inject(OrganizationPlatformBillingStore);
    private readonly dialogService = inject(BaseDialogService);
    private readonly confirm = inject(ConfirmBuilder);
    protected readonly canCreate = isAllowed['organizationBilling.create'];
    protected readonly canUpdate = isAllowed['organizationBilling.update'];
    protected readonly canDelete = isAllowed['organizationBilling.delete'];
    protected readonly prefix = 'organization.platformBilling.';
    protected readonly basisStates = PlatformBillingBasisStateEnum;
    protected readonly invoiceStates = PlatformBillingInvoiceStateEnum;
    protected readonly sendingBasisId = signal<number | null>(null);
    protected readonly sendingInvoiceId = signal<number | null>(null);
    private readonly tables = viewChildren(TableComponent);
    protected readonly refreshContracts = (input: GridInput) => this.store.api.platformBillingContracts(this.organizationId(), input);
    protected readonly refreshBases = (input: GridInput) => this.store.api.platformBillingBases(this.organizationId(), input);
    protected readonly refreshInvoices = (input: GridInput) => this.store.api.platformBillingInvoices(this.organizationId(), input);

    protected readonly profileModel = signal<PlatformBillingProfileFormModel>({
        billingPeriod: PlatformBillingPeriodEnum.MONTHLY,
        billingEmails: [],
        billingFrom: new Date(),
        billingTo: null,
        automaticBasis: false,
        automaticInvoicing: false,
        recipientCenterId: null,
        recipientAddressId: null,
        destinationCenterId: null,
        destinationAddressId: null,
        billingAddressOverride: '',
    });
    protected readonly profileForm = form(this.profileModel, (schema) => {
        required(schema.billingPeriod);
        required(schema.billingFrom);
        for (const path of [schema.recipientCenterId, schema.destinationCenterId]) {
            validate(path, ({ value }) => value() == null || this.centerOptions().some((option) => option.value === value())
                ? null : { kind: 'center', message: 'Vyberte středisko této organizace.' });
        }
        validate(schema.recipientAddressId, ({ value }) => value() == null || this.recipientAddressOptions().some((option) => option.value === value())
            ? null : { kind: 'address', message: 'Vyberte adresu zvoleného střediska.' });
        validate(schema.destinationAddressId, ({ value }) => value() == null || this.destinationAddressOptions().some((option) => option.value === value())
            ? null : { kind: 'address', message: 'Vyberte adresu zvoleného střediska.' });
    });

    protected readonly centerOptions = computed(() => (this.organization()?.centers ?? []).map((center) => ({
        value: center.id,
        name: this.currentCenterName(center),
    })));
    protected readonly recipientAddressOptions = computed(() => this.addressOptions(this.profileModel().recipientCenterId));
    protected readonly destinationAddressOptions = computed(() => this.addressOptions(this.profileModel().destinationCenterId));

    protected clearRecipientAddress(): void {
        this.profileModel.update((model) => ({ ...model, recipientAddressId: null }));
    }

    protected clearDestinationAddress(): void {
        this.profileModel.update((model) => ({ ...model, destinationAddressId: null }));
    }

    private currentCenterName(center: CenterObject): string {
        const today = this.toDateString(new Date())!;
        const info = [...(center.basicInfos ?? [])]
            .filter((item) => item.validFrom <= today && (!item.validTo || item.validTo >= today))
            .sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0];
        return info?.name || `Středisko ${center.id}`;
    }

    private addressOptions(centerId: number | null): { value: number; name: string }[] {
        const centers = this.organization()?.centers ?? [];
        const center = centerId == null ? centers.find((item) => item.primary) ?? centers[0] : centers.find((item) => item.id === centerId);
        return (center?.addresses ?? []).map((address) => ({
            value: address.id,
            name: `${address.address?.name || address.address?.location || address.id} (${address.validFrom} – ${address.validTo || '…'})`,
        }));
    }

    protected readonly periodOptions = [
        { value: PlatformBillingPeriodEnum.MONTHLY, name: 'Měsíční' },
        { value: PlatformBillingPeriodEnum.QUARTERLY, name: 'Čtvrtletní' },
        { value: PlatformBillingPeriodEnum.YEARLY, name: 'Roční' },
    ];
    protected readonly tariffOptions = computed<PlatformBillingTariffSelectOption[]>(() => (this.store.data.systemOverview()?.tariffs ?? []).map((tariff) => ({
        value: tariff.id,
        name: tariff.name,
        tariff,
    })));
    protected readonly sequenceOptions = computed(() => (this.store.data.systemOverview()?.numberSequences ?? [])
        .filter((sequence) => sequence.active)
        .map((sequence) => ({ value: sequence.id, name: sequence.name })));
    protected readonly serviceOptions = computed(() => {
        const services = new Map<number, string>();
        (this.store.data.organizationOverview()?.contracts ?? []).forEach((contract) => {
            if (!services.has(contract.serviceId)) {
                services.set(contract.serviceId, contract.serviceName || contract.serviceTypeName || '-');
            }
        });
        return [...services].map(([value, name]) => ({ value, name }));
    });
    protected readonly basisCreateDisabledReason = computed<string | null>(() => {
        const overview = this.store.data.organizationOverview();
        if (!overview) return null;
        if (!overview.profile) return `${this.prefix}warnings.missingSettings`;
        if (!overview.contracts.length) return `${this.prefix}warnings.missingServices`;
        return null;
    });
    protected readonly invoiceCreateDisabledReason = computed<string | null>(() => {
        const basisReason = this.basisCreateDisabledReason();
        if (basisReason) return basisReason;
        if (!this.store.data.organizationOverview()) return null;
        if (!this.sequenceOptions().length) return `${this.prefix}warnings.missingNumberSequence`;
        return null;
    });
    protected readonly configurationWarning = computed(() => this.invoiceCreateDisabledReason());
    protected readonly contractGrid = computed(() => {
        const grid = this.store.data.platformBillingContracts();
        return grid ? { ...grid, items: grid.items.map((contract): PlatformBillingContractTableItem => ({
            id: contract.id,
            contract,
            serviceName: contract.serviceName || contract.serviceTypeName || '-',
            state: translate.instant(`${this.prefix}states.${contract.state}`),
            serviceType: contract.serviceTypeName,
            monthlyUserPrice: contract.monthlyUserPriceOverride,
            fixedServicePrice: contract.fixedPriceOverride,
            validFrom: contract.validFrom,
            validTo: contract.validTo,
            discountPercentage: contract.discountPercentage,
        })) } : null;
    });
    protected readonly basisGrid = computed(() => {
        const grid = this.store.data.platformBillingBases();
        return grid ? { ...grid, items: grid.items.map((basis): PlatformBillingBasisTableItem => ({
            id: basis.id,
            basis,
            serviceName: basis.serviceName,
            serviceType: basis.serviceTypeName,
            amountBeforeDiscount: basis.amountBeforeDiscount,
            discountPercentage: basis.discountPercentage,
            netAmount: basis.netAmount,
            period: this.formatBasisPeriod(basis.periodFrom, basis.periodTo),
            actions: null,
            state: translate.instant(`${this.prefix}states.${basis.state}`),
            canReplace: basis.canReplace,
            deliveryFailed: basis.deliveries.some((delivery) => delivery.state === PlatformBillingDeliveryStateEnum.FAILED),
        })) } : null;
    });
    private readonly loadedInvoiceItems = signal<Record<number, PlatformBillingInvoiceItemTableItem[]>>({});
    private readonly loadingInvoiceIds = signal<Set<number>>(new Set());
    protected readonly exportingInvoice = signal<{ invoiceId: number; format: 'pdf' | 'isdoc' } | null>(null);
    protected readonly invoiceGrid = computed(() => {
        const grid = this.store.data.platformBillingInvoices();
        return grid ? { ...grid, items: grid.items.map((invoice): PlatformBillingInvoiceTableItem => ({
            id: invoice.id,
            invoice,
            invoiceNumber: invoice.invoiceNumber,
            state: translate.instant(`${this.prefix}states.${invoice.state}`),
            deliveryFailed: invoice.deliveries.some((delivery) => delivery.state === PlatformBillingDeliveryStateEnum.FAILED),
            totalAmount: invoice.totalAmount,
            issueDate: invoice.issueDate,
            periodFrom: invoice.periodFrom,
            periodTo: invoice.periodTo,
            issuedBy: invoice.issuer ?? null,
            actions: null,
            items: this.loadedInvoiceItems()[invoice.id] ?? null,
            itemsLoading: this.loadingInvoiceIds().has(invoice.id),
        })) } : null;
    });
    protected readonly contractColumns: TableColumn[] = [
        textColumn('serviceName'),
        textColumn('serviceType'),
        this.stateColumn(PlatformBillingContractStateEnum),
        { name: 'monthlyUserPrice', type: TableColumnType.Monetary, filterable: { type: 'number' }, sortable: true },
        { name: 'fixedServicePrice', type: TableColumnType.Monetary, filterable: { type: 'number' }, sortable: true },
        validFromColumn,
        validToColumn,
        numberField('discountPercentage'),
    ];
    protected readonly basisColumns: TableColumn[] = [
        textColumn('serviceName'),
        textColumn('serviceType'),
        this.stateColumn(PlatformBillingBasisStateEnum),
        { name: 'amountBeforeDiscount', type: TableColumnType.Monetary, filterable: { type: 'number' }, sortable: true },
        numberField('discountPercentage'),
        { name: 'netAmount', type: TableColumnType.Monetary, filterable: { type: 'number' }, sortable: true },
        textColumn('period'),
        textColumn('actions', false, false),
    ];
    protected readonly invoiceColumns: TableColumn[] = [
        textColumn('invoiceNumber'),
        this.stateColumn(PlatformBillingInvoiceStateEnum),
        { name: 'totalAmount', type: TableColumnType.Monetary, filterable: { type: 'number' }, sortable: true },
        dateField('issueDate'),
        dateField('periodFrom'),
        dateField('periodTo'),
        userChipField('issuedBy', UserChipRenderTypeEnum.User),
        textColumn('actions', false, false),
    ];
    protected readonly invoiceItemColumns: TableColumn[] = [
        textColumn('name'),
        dateField('issueDate'),
        { name: 'amount', type: TableColumnType.Monetary, filterable: { type: 'number' }, sortable: true },
        dateField('periodFrom'),
        dateField('periodTo'),
        userChipField('issuedBy', UserChipRenderTypeEnum.User),
    ];
    constructor() {
        effect(() => {
            const organizationId = this.organizationId();
            void this.reload(organizationId);
        });
        effect(() => {
            const overview = this.store.data.organizationOverview();
            if (!overview) return;
            const profile = overview.profile;
            if (profile) {
                this.profileForm().reset({
                    billingPeriod: profile.billingPeriod as PlatformBillingPeriodEnum,
                    billingEmails: [...profile.billingEmails],
                    billingFrom: this.toDate(profile.billingFrom),
                    billingTo: this.toDate(profile.billingTo),
                    automaticBasis: profile.automaticBasis,
                    automaticInvoicing: profile.automaticInvoicing,
                    recipientCenterId: profile.recipientCenterId,
                    recipientAddressId: profile.recipientAddressId,
                    destinationCenterId: profile.destinationCenterId,
                    destinationAddressId: profile.destinationAddressId,
                    billingAddressOverride: profile.billingAddressOverride ?? '',
                });
            }
        });
    }

    protected async saveProfile(): Promise<void> {
        if (!this.validate(this.profileForm)) return;
        const model = this.profileModel();
        const billingFrom = this.toDateString(model.billingFrom);
        if (!billingFrom) return;
        await this.store.api.upsertPlatformBillingOrganizationProfile({
            organizationId: this.organizationId(),
            billingPeriod: model.billingPeriod,
            billingEmails: model.billingEmails,
            billingTo: this.toDateString(model.billingTo),
            billingFrom,
            automaticBasis: model.automaticBasis || model.automaticInvoicing,
            automaticInvoicing: model.automaticInvoicing,
            recipientCenterId: model.recipientCenterId,
            recipientAddressId: model.recipientAddressId,
            destinationCenterId: model.destinationCenterId,
            destinationAddressId: model.destinationAddressId,
            billingAddressOverride: model.billingAddressOverride || null,
        });
        await this.reload();
        successUpdated();
    }

    protected openContractUpsertDialog(item: PlatformBillingContractTableItem | null = null): void {
        this.dialogService.open(OrganizationPlatformBillingContractUpsertDialogComponent, {
            header: translate.instant(`${this.prefix}contractDialog.${item ? 'titleEdit' : 'titleCreate'}`),
            styleClass: 'p-dialog-width-60',
            data: {
                item: item?.contract ?? null,
                organizationId: this.organizationId(),
                serviceOptions: this.serviceOptions(),
                tariffOptions: this.tariffOptions(),
            },
        }).onClose.subscribe(() => void this.reload());
    }

    protected openBasisCreateDialog(basis: PlatformBillingBasisObject | null = null): void {
        if (this.basisCreateDisabledReason()) return;
        this.dialogService.open(OrganizationPlatformBillingBasisCreateDialogComponent, {
            header: translate.instant(`${this.prefix}basisDialog.${basis ? 'titleReplace' : 'titleCreate'}`),
            styleClass: 'p-dialog-width-60',
            data: {
                organizationId: this.organizationId(),
                serviceOptions: this.serviceOptions(),
                basis,
            },
        }).onClose.subscribe((saved) => saved && void this.reload());
    }

    protected async exportBasis(basis: PlatformBillingBasisObject): Promise<void> {
        const file = await this.store.api.exportPlatformBillingBasis({ id: basis.id });
        downloadFile(file.content, file.fileName, file.contentType);
    }

    protected async sendBasis(basis: PlatformBillingBasisObject): Promise<void> {
        if (!this.canUpdate() || this.sendingBasisId() !== null || basis.state === PlatformBillingBasisStateEnum.CANCELLED) return;
        this.sendingBasisId.set(basis.id);
        try {
            await this.store.api.sendPlatformBillingBasis({ id: basis.id });
        } finally {
            this.sendingBasisId.set(null);
            await this.reload();
        }
    }

    protected deleteBasis(event: TableDeleteEvent<PlatformBillingBasisTableItem>): void {
        this.confirm.confirmDelete(async () => {
            await this.store.api.cancelPlatformBillingBasis({ id: event.item.id });
            await this.reload();
            successDeleted();
        }, event);
    }

    protected openInvoiceCreateDialog(): void {
        if (this.invoiceCreateDisabledReason()) return;
        this.dialogService.open(OrganizationPlatformBillingInvoiceCreateDialogComponent, {
            header: translate.instant(`${this.prefix}invoiceDialog.titleCreate`),
            styleClass: 'p-dialog-width-60',
            data: {
                organizationId: this.organizationId(),
                numberSequenceId: this.sequenceOptions()[0]?.value ?? null,
            },
        }).onClose.subscribe((saved) => saved && void this.reload());
    }

    protected async exportInvoice(invoice: PlatformBillingInvoiceObject, format: 'pdf' | 'isdoc'): Promise<void> {
        if (this.exportingInvoice()) return;
        this.exportingInvoice.set({ invoiceId: invoice.id, format });
        try {
            const file = await this.store.api.exportPlatformBillingInvoice({ id: invoice.id, format });
            downloadFile(file.content, file.fileName, file.contentType);
        } finally {
            this.exportingInvoice.set(null);
        }
    }

    protected async loadInvoiceItems(event: { expanded: boolean; item: PlatformBillingInvoiceTableItem }): Promise<void> {
        const invoiceId = event.item.id;
        if (event.expanded || this.loadedInvoiceItems()[invoiceId] || this.loadingInvoiceIds().has(invoiceId)) return;
        this.loadingInvoiceIds.update((ids) => new Set(ids).add(invoiceId));
        try {
            const items = await this.store.api.invoiceItems(invoiceId);
            this.loadedInvoiceItems.update((loaded) => ({
                ...loaded,
                [invoiceId]: items.map((item): PlatformBillingInvoiceItemTableItem => ({
                    id: item.id,
                    name: item.name,
                    issueDate: event.item.issueDate,
                    amount: item.totalAmount,
                    periodFrom: item.periodFrom,
                    periodTo: item.periodTo,
                    issuedBy: event.item.issuedBy,
                })),
            }));
        } finally {
            this.loadingInvoiceIds.update((ids) => {
                const next = new Set(ids);
                next.delete(invoiceId);
                return next;
            });
        }
    }

    protected async sendInvoice(invoice: PlatformBillingInvoiceObject): Promise<void> {
        if (!this.canUpdate() || this.sendingInvoiceId() !== null || invoice.state === PlatformBillingInvoiceStateEnum.CANCELLED) return;
        this.sendingInvoiceId.set(invoice.id);
        try {
            await this.store.api.sendPlatformBillingInvoice({ id: invoice.id });
        } finally {
            this.sendingInvoiceId.set(null);
            await this.reload();
        }
    }

    protected deleteInvoice(event: TableDeleteEvent<PlatformBillingInvoiceTableItem>): void {
        this.confirm.confirmDelete(async () => {
            await this.store.api.cancelPlatformBillingInvoice({ id: event.item.id });
            await this.reload();
            successDeleted();
        }, event);
    }

    private async reload(organizationId = this.organizationId()): Promise<void> {
        await Promise.all([
            this.store.api.organizationOverview(organizationId),
            this.store.api.systemOverview(false),
        ]);
        await Promise.all(this.tables().map((table) => table.reload()));
    }

    private stateColumn(states: Record<string, string>): TableColumn {
        return {
            ...textColumn('state'),
            filterable: { type: 'select', selectOptions: () => Object.values(states).map((value) => ({ value, name: translate.instant(`${this.prefix}states.${value}`) })) },
        };
    }

    private formatBasisPeriod(periodFrom: string, periodTo: string): string {
        const from = new Date(`${periodFrom}T12:00:00`);
        const to = new Date(`${periodTo}T12:00:00`);
        const lastDay = new Date(from.getFullYear(), from.getMonth() + 1, 0).getDate();
        if (from.getDate() === 1 && to.getDate() === lastDay && from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
            return new Intl.DateTimeFormat('cs-CZ', { month: 'long', year: 'numeric' }).format(from);
        }
        return `${from.toLocaleDateString('cs-CZ')}–${to.toLocaleDateString('cs-CZ')}`;
    }

    private validate(fieldForm: { (): { invalid(): boolean; markAsTouched(): void } }): boolean {
        if (!fieldForm().invalid()) return true;
        fieldForm().markAsTouched();
        return false;
    }

    private toDate(value: string | null): Date | null {
        return value ? new Date(`${value}T12:00:00`) : null;
    }

    private toDateString(value: Date | null): string | null {
        if (!value) return null;
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
